import { Component } from '@theme/component';

/**
 * Category cards carousel ("Pick your HallPass").
 *
 * On mobile the track scrolls full-bleed — the cards reach the screen edges so the
 * peeking card is never clipped by the page margin. The native scrollbar is hidden
 * (it would span that full-bleed width); instead this component draws a custom 1px
 * scrollbar that stays at page width, mirrors the track's scroll position onto a
 * thumb, and lets the shopper drag the bar (or tap it) to scroll.
 *
 * On desktop the cards are an equal-width grid with no overflow, so the bar hides
 * itself.
 */
class HpCategoryCards extends Component {
  #dragging = false;

  connectedCallback() {
    super.connectedCallback();
    this.refs.track?.addEventListener('scroll', this.#update, { passive: true });
    this.refs.scrollbar?.addEventListener('pointerdown', this.#onPointerDown);
    window.addEventListener('resize', this.#update, { passive: true });
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.track?.removeEventListener('scroll', this.#update);
    this.refs.scrollbar?.removeEventListener('pointerdown', this.#onPointerDown);
    window.removeEventListener('resize', this.#update);
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
  }

  /** Maximum horizontal scroll distance of the track. */
  get #maxScroll() {
    const track = this.refs.track;
    return track ? Math.max(0, track.scrollWidth - track.clientWidth) : 0;
  }

  /** Reflect the track's scroll position onto the thumb (size + position). */
  #update = () => {
    const { track, scrollbar, thumb } = this.refs;
    if (!track || !scrollbar || !thumb) return;

    // Nothing to scroll (e.g. desktop grid) → hide the whole bar.
    const scrollable = track.scrollWidth > track.clientWidth + 1;
    scrollbar.hidden = !scrollable;
    if (!scrollable) return;

    const barWidth = scrollbar.clientWidth;
    const thumbWidth = Math.max(24, barWidth * (track.clientWidth / track.scrollWidth));
    const maxTravel = barWidth - thumbWidth;
    const progress = this.#maxScroll ? track.scrollLeft / this.#maxScroll : 0;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${progress * maxTravel}px)`;
  };

  /** Map a pointer x within the bar to a track scroll position. */
  #scrollToPointer(clientX) {
    const { track, scrollbar, thumb } = this.refs;
    if (!track || !scrollbar || !thumb) return;
    const rect = scrollbar.getBoundingClientRect();
    const thumbWidth = thumb.getBoundingClientRect().width;
    const maxTravel = rect.width - thumbWidth;
    if (maxTravel <= 0) return;
    const offset = clientX - rect.left - thumbWidth / 2;
    const progress = Math.min(1, Math.max(0, offset / maxTravel));
    track.scrollLeft = progress * this.#maxScroll;
  }

  #onPointerDown = (event) => {
    if (!this.refs.track) return;
    event.preventDefault();
    this.#dragging = true;
    this.refs.scrollbar?.setPointerCapture?.(event.pointerId);
    this.#scrollToPointer(event.clientX);
    window.addEventListener('pointermove', this.#onPointerMove);
    window.addEventListener('pointerup', this.#onPointerUp);
  };

  #onPointerMove = (event) => {
    if (this.#dragging) this.#scrollToPointer(event.clientX);
  };

  #onPointerUp = () => {
    this.#dragging = false;
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
  };
}

if (!customElements.get('hp-category-cards')) {
  customElements.define('hp-category-cards', HpCategoryCards);
}
